import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailCheck } from 'lucide-react';

import { getStoredEmailClaim, recheckEmailVerified } from '@/lib/logto';
import { useAppSelector } from '@/store/hooks';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { OSCURA } from '@/lib/v3';

interface EmailGateStepProps {
  onVerified: () => void;
}

/**
 * Hard gate shown only when the id_token says email_verified === false.
 * "I've verified" refreshes the token pair (fresh claims) and re-checks.
 * An unknown result (null) lets the user through — never block on a
 * best-effort signal.
 */
export function EmailGateStep({ onVerified }: EmailGateStepProps) {
  const { t } = useTranslation('onboarding');
  const storeEmail = useAppSelector((s) => s.auth.user?.email);
  const email = getStoredEmailClaim() ?? storeEmail ?? '';
  const haptics = useHaptics();
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);

  const recheck = async () => {
    setChecking(true);
    setFailed(false);
    const verified = await recheckEmailVerified();
    setChecking(false);
    if (verified === false) {
      haptics.notification('error');
      setFailed(true);
      return;
    }
    haptics.notification('success');
    onVerified();
  };

  useMainButton({
    text: checking ? t('email.checking') : t('email.cta'),
    enabled: !checking,
    loading: checking,
    onClick: () => void recheck(),
  });

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/10 text-brand">
        <MailCheck aria-hidden className="h-8 w-8" />
      </span>
      <h1 className="text-2xl font-bold text-fg">{t('email.title')}</h1>
      <p className="text-sm text-fg-muted" {...OSCURA}>{t('email.body', { email })}</p>
      <p className="text-xs text-fg-muted">{t('email.hint')}</p>
      {failed && <p className="text-sm text-danger">{t('email.stillUnverified')}</p>}
    </div>
  );
}
